import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import TrainingService from '../services/TrainingService';
import { normalizeImageUrl, fixHtmlImagePaths, processEmbedUrls } from '../utils/imageUtils';
import { useData } from '../context/DataContext';
import { updateMetaTags } from '../utils/seoUtils';
import useTikTokEmbed from '../utils/useTikTokEmbed';
import ReviewSection from '../components/ReviewSection';

const TrainingDetailPage = () => {
    const { slug } = useParams();
    const { settings } = useData();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedCourses, setRelatedCourses] = useState([]);

    const lastFetchedSlugRef = useRef(null);
    const isFetchingRef = useRef(false);
    const contentRef = useRef(null);

    useTikTokEmbed(contentRef, [course]);

    useEffect(() => {
        if (course) {
            updateMetaTags({
                title: course.title,
                description: course.shortDescription || course.description || course.title,
                image: course.thumbnailUrl || course.image || course.thumbnail || course.imageUrl,
            });
        }
    }, [course]);

    useEffect(() => {
        const fetchCourseDetail = async () => {
            if (isFetchingRef.current) return;
            if (lastFetchedSlugRef.current === slug) return;

            isFetchingRef.current = true;
            setLoading(true);
            try {
                const [fullDetail, allCourses] = await Promise.all([
                    TrainingService.getCourseBySlug(slug),
                    TrainingService.getAllCourses()
                ]);

                setCourse(fullDetail);
                lastFetchedSlugRef.current = slug;

                if (allCourses && Array.isArray(allCourses)) {
                    setRelatedCourses(allCourses
                        .filter(c => (
                            c.status === 'PUBLISHED' &&
                            c.slug !== slug &&
                            (c._id || c.id) !== (fullDetail?._id || fullDetail?.id)
                        ))
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .slice(0, 8)
                    );
                }
            } catch (error) {
                console.error("Lỗi tải chi tiết khóa đào tạo:", error);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        if (slug) {
            fetchCourseDetail();
            window.scrollTo(0, 0);
        }
    }, [slug]);

    if (loading) {
        return <div className="text-center py-5">Đang tải...</div>;
    }

    if (!course) {
        return (
            <div className="text-center py-5">
                <h2>Không tìm thấy khóa đào tạo</h2>
                <Link to="/dao-tao">Quay lại danh sách</Link>
            </div>
        );
    }

    return (
        <main style={{ backgroundColor: '#f4f7f6', color: '#333', paddingBottom: '100px' }}>
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li className="breadcrumb-item"><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item"><Link to="/dao-tao" className="hover:text-red-600 transition-colors no-underline text-slate-500">Đào tạo</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item active text-slate-900 font-bold" aria-current="page">{course.title}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <div className="row" style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div className="col-lg-9 col-md-12 mb-4">
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '30px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%'
                        }}>
                            <h1 style={{
                                fontSize: '26px',
                                fontWeight: '800',
                                marginBottom: '20px',
                                color: '#ff0000',
                                lineHeight: '1.4',
                                wordBreak: 'break-word'
                            }}>
                                {course.title}
                            </h1>

                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '15px',
                                color: '#666',
                                fontSize: '14px',
                                borderBottom: '1px solid #f0f0f0',
                                paddingBottom: '15px',
                                marginBottom: '25px'
                            }}>
                                <span className="d-flex align-items-center gap-1"><i className="fa fa-calendar text-danger"></i> Ngày đăng: {course.createdAt ? new Date(course.createdAt).toLocaleDateString('vi-VN') : 'Mới cập nhật'}</span>
                                <span className="d-flex align-items-center gap-1"><i className="fa fa-bookmark text-primary"></i> Đào tạo kỹ thuật</span>
                            </div>

                            <div className="course-content ql-editor" style={{
                                fontSize: '16px',
                                lineHeight: '1.8',
                                color: '#444',
                                overflow: 'hidden',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                            }}>
                                {course.content ? (
                                    <div
                                        ref={contentRef}
                                        className="course-content-inner"
                                        style={{ maxWidth: '100%', wordBreak: 'normal', overflowWrap: 'break-word', wordWrap: 'break-word' }}
                                        dangerouslySetInnerHTML={{ __html: processEmbedUrls(fixHtmlImagePaths(course.content.replace(/&nbsp;/g, ' '))) }}
                                    />
                                ) : (
                                    <p>{course.content || 'Nội dung đào tạo hiện đang được cập nhật...'}</p>
                                )}
                            </div>

                            <div style={{
                                marginTop: '40px',
                                padding: '30px',
                                background: '#f8f9fa',
                                borderRadius: '12px',
                                border: '1px solid #e9ecef',
                                textAlign: 'center'
                            }}>
                                <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Đăng ký nhận tư vấn về khóa học này?</h4>
                                <div className="d-flex justify-content-center gap-3 flex-wrap">
                                    <Link to="/lien-he" className="btn btn-primary btn-lg rounded-pill px-5 fw-bold shadow-sm btn-blink-orange">
                                        Đăng ký học ngay
                                    </Link>
                                    <a href={`tel:${settings?.hotline?.replace(/[^0-9]/g, '')}`} className="btn btn-outline-dark btn-lg rounded-pill px-5 fw-bold" style={{ border: '2px solid #333' }}>
                                        {settings?.hotline}
                                    </a>
                                </div>
                            </div>

                            {/* Review & Rating Section */}
                            <ReviewSection
                                targetType="course"
                                targetId={slug || course?._id}
                                targetTitle={course?.title}
                            />
                        </div>
                    </div>

                    <div className="col-lg-3 d-none d-lg-block" style={{ position: 'sticky', top: '100px' }}>
                        <div style={{ width: '100%' }}>
                            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '25px' }}>
                                <h5 style={{ fontSize: '16px', fontWeight: 'bold', borderBottom: '2px solid #ff0000', paddingBottom: '10px', marginBottom: '20px' }}>THÔNG TIN ĐÀO TẠO</h5>

                                <div className="mb-3">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Thời gian học</small>
                                    <span style={{ fontWeight: '600' }}>Theo lịch trung tâm</span>
                                </div>

                                <div className="mb-3">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Đối tượng học viên</small>
                                    <span style={{ fontWeight: '600' }}>Kỹ thuật viên, Sinh viên</span>
                                </div>

                                <div className="mb-0">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Địa điểm đào tạo</small>
                                    <span style={{ fontWeight: '600' }}>Hà Nội</span>
                                </div>
                            </div>

                            {relatedCourses.length > 0 && (
                                <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <h5 style={{ fontSize: '16px', fontWeight: 'bold', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '20px' }}>MỚI NHẤT</h5>

                                    {relatedCourses.slice(0, 3).map((rc, idx) => (
                                        <div key={idx} style={{ paddingBottom: '15px', marginBottom: '15px', borderBottom: idx < 2 ? '1px dashed #eee' : 'none' }}>
                                            <Link to={`/dao-tao/${rc.slug}`} style={{ color: '#333', textDecoration: 'none', fontWeight: '700', fontSize: '14px', display: 'block', lineHeight: '1.4' }}>
                                                {rc.title}
                                            </Link>
                                            <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: '12px', color: '#888' }}>
                                                <span><i className="fa fa-calendar me-1"></i> {rc.createdAt ? new Date(rc.createdAt).toLocaleDateString('vi-VN') : 'Mới'}</span>
                                            </div>
                                        </div>
                                    ))}

                                    <Link to="/dao-tao" className="btn btn-light w-100 fw-bold border" style={{ fontSize: '13px' }}>
                                        Tất cả khóa học <i className="fa fa-angle-right ms-1"></i>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {relatedCourses.length > 0 && (
                    <div style={{ marginTop: '60px' }}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '800',
                            marginBottom: '30px',
                            textAlign: 'center',
                            textTransform: 'uppercase',
                            position: 'relative',
                            paddingBottom: '15px'
                        }}>
                            Khóa đào tạo liên quan
                            <span style={{
                                content: '""',
                                position: 'absolute',
                                bottom: '0',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60px',
                                height: '3px',
                                backgroundColor: '#ff0000'
                            }}></span>
                        </h3>

                        <div className="row">
                            {relatedCourses.map((rc, idx) => (
                                <div key={idx} className="col-lg-3 col-md-6 mb-4">
                                    <div style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        height: '100%',
                                        transition: 'transform 0.3s ease'
                                    }} className="course-card-hover">
                                        <Link to={`/dao-tao/${rc.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                            <div style={{ position: 'relative', height: '180px', overflow: 'hidden' }}>
                                                <img
                                                    src={normalizeImageUrl(rc.thumbnailUrl || rc.image || rc.thumbnail || '/assets/default-course.jpg')}
                                                    alt={rc.title}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                                <h4 style={{
                                                    fontSize: '15px',
                                                    fontWeight: '700',
                                                    lineHeight: '1.5',
                                                    height: '45px',
                                                    overflow: 'hidden',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    marginBottom: '10px'
                                                }}>
                                                    {rc.title}
                                                </h4>
                                                <div style={{ color: '#888', fontSize: '12px' }}>
                                                    <i className="fa fa-calendar me-1"></i>
                                                    {rc.createdAt ? new Date(rc.createdAt).toLocaleDateString('vi-VN') : 'Mới cập nhật'}
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .course-content img { max-width: 100% !important; height: auto !important; }
                .course-content iframe { max-width: 100% !important; }
                .course-content table { width: 100% !important; overflow-wrap: break-word; }
                .course-content * { 
                    word-break: normal !important; 
                    overflow-wrap: break-word !important; 
                    word-wrap: break-word !important; 
                }
                
                @keyframes blink-orange {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
                    50% { transform: scale(1.05); box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.4); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
                }
                
                .btn-blink-orange {
                    background-color: #ff0000 !important;
                    border-color: #ff0000 !important;
                    color: #fff !important;
                    animation: blink-orange 1.5s infinite ease-in-out;
                    transition: all 0.3s ease;
                }
                
                .btn-blink-orange:hover {
                    background-color: #e57300 !important;
                    transform: scale(1.1) !important;
                    box-shadow: 0 0 20px rgba(255, 0, 0, 0.6) !important;
                }

                @media (max-width: 991px) {
                    .col-lg-3 { position: static !important; }
                }
            `}</style>
        </main>
    );
};

export default TrainingDetailPage;
